import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { useLingui } from "@lingui/react";
import { locale } from "src/config/app-config";
import {
  useListingServerSnapshot,
  useListingState,
} from "src/hooks/stores/use-listing-store";
import {
  buildCategoryTree,
  omitParentSlugs,
  ROOT_PARENT_ID,
} from "src/utils/category-tree-helpers";
import {
  categoryHref,
  getCategoryLevelView,
  type CategoryLevelView,
} from "src/utils/category-navigation";
import {
  isGender,
  localeFix,
  localeFixOptional,
  ShopType,
} from "src/utils/genderLocale/genderLocale";
import { LISTING_KEY_TO_QUERY_PARAM } from "src/hooks/use-handle-search-params";
import getBrandPageRoute from "src/utils/getBrandPageRoute";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { capitalize } from "src/utils/helper/string";
import type { Gender, ProductListType } from "src/types/product-listing";

type UseCategoryLevelView = {
  view: CategoryLevelView;
  /** false until the listing facets are available (render a skeleton meanwhile) */
  isReady: boolean;
  drillsByFilter: boolean;
};

const CATEGORY_PARAM = LISTING_KEY_TO_QUERY_PARAM.category;

/** PLPs that are not category routes: they drill with ?cat_slug=, in place. */
const FILTER_DRILLED_PLP_TYPES: ProductListType[] = [
  "SEARCH_PLP",
  "BRAND_PLP",
  "NEW_IN_PLP",
  "SALE_PLP",
  "HOT_DEALS_PLP",
  "DESIGNER_PLP",
  "COLLECTIONS_PLP",
];

/**
 * The one drill-down level to render, derived from the listing's `category`
 * facet — hydrated from getStaticProps, so the links are server-rendered.
 *
 * Category and shop-type routes drill by URL, keeping their links crawlable.
 * Every other PLP (and a shop-type PLP that already carries a category filter)
 * drills with ?cat_slug= and stays put; brand pages send department roots to
 * /merken/<brand>/<gender>, elsewhere they clear the filter.
 *
 * The tree comes from the store, frozen from the listing's first unfiltered
 * response, so drilling and backing out never depend on what the current
 * filters left behind. Filter-drilled levels are intersected with the live
 * facet to drop emptied categories; a level left with one option, or none, is
 * replaced by the level itself — a lone subcategory renames, it doesn't refine.
 */
export function useCategoryLevelView({
  gender,
  categorySlug,
  pageContentType,
}: {
  gender?: Gender;
  categorySlug?: string;
  pageContentType?: ProductListType;
}): UseCategoryLevelView {
  const { i18n } = useLingui();
  const {
    facets: liveFacets,
    tree: storeTree,
    appliedFacets,
    hasFetched,
  } = useListingState();
  const serverSnapshot = useListingServerSnapshot();
  const facets = liveFacets ?? serverSnapshot.facets;
  const router = useRouter();
  const resolvedGender = localeFixOptional({ gender }) || ShopType.All;
  const isSearch = pageContentType === "SEARCH_PLP";
  const isBrand = pageContentType === "BRAND_PLP";
  const isShopType = pageContentType === "SHOP_TYPE_PLP";
  const drillsByFilter =
    (!!pageContentType && FILTER_DRILLED_PLP_TYPES.includes(pageContentType)) ||
    (isShopType && (appliedFacets.category?.length ?? 0) > 0);

  const facetTree = useMemo(() => {
    const categoryFacet = facets?.get("category");

    if (categoryFacet?.type !== "category") {
      return [];
    }

    const categories =
      resolvedGender !== ShopType.All
        ? categoryFacet.data.filter((item) =>
            item.genders.includes(localeFix({ gender: resolvedGender }))
          )
        : categoryFacet.data;

    return buildCategoryTree(categories);
  }, [facets, resolvedGender]);

  // facetTree only covers the first render, before the store has frozen its own
  const tree = storeTree ?? facetTree;

  const filterHref = useCallback(
    (slug?: string, basePath?: string): string => {
      const url = new URL(router.asPath, "http://internal");

      url.searchParams.delete("page");

      if (slug) {
        url.searchParams.set(CATEGORY_PARAM, slug);
      } else {
        url.searchParams.delete(CATEGORY_PARAM);
      }

      return `${basePath ?? url.pathname}${url.search}`;
    },
    [router.asPath]
  );

  const persistedSearch = useMemo(() => {
    if (!router.isReady) {
      return "";
    }

    const { searchParams } = new URL(router.asPath, "http://internal");

    searchParams.delete("page");
    searchParams.delete(CATEGORY_PARAM);

    const search = searchParams.toString();

    return search ? `?${search}` : "";
  }, [router.isReady, router.asPath]);

  const routeHref = useCallback(
    (slug: string): string =>
      `${categoryHref(slug, resolvedGender, locale)}${persistedSearch}`,
    [persistedSearch, resolvedGender]
  );

  const buildHref = useMemo(() => {
    if (isBrand) {
      const brandSlug = categorySlug ?? "";
      const brandBasePath =
        resolvedGender !== ShopType.All
          ? getBrandPageRoute(resolvedGender, brandSlug, locale)
          : `${getI18nRoute({ route: "/brands", locale })}/${brandSlug}`;

      return (slug: string): string =>
        isGender(slug)
          ? getBrandPageRoute(localeFix({ gender: slug }), brandSlug, locale)
          : filterHref(slug, brandBasePath);
    }

    if (drillsByFilter) {
      return resolvedGender === ShopType.All
        ? filterHref
        : (slug: string): string =>
            isGender(slug) ? filterHref() : filterHref(slug);
    }

    return routeHref;
  }, [
    drillsByFilter,
    isBrand,
    filterHref,
    routeHref,
    categorySlug,
    resolvedGender,
  ]);

  const rootLabel = useMemo(() => {
    if (!drillsByFilter || isBrand) {
      return undefined;
    }

    if (resolvedGender === ShopType.All) {
      return isSearch ? String(router.query.q ?? "") : undefined;
    }

    const root = tree.find(
      (node) =>
        node.parentId === ROOT_PARENT_ID &&
        isGender(node.slug) &&
        localeFix({ gender: node.slug }) === resolvedGender
    );

    return root?.label ?? i18n._(capitalize(resolvedGender));
  }, [
    drillsByFilter,
    isBrand,
    isSearch,
    resolvedGender,
    tree,
    router.query.q,
    i18n,
  ]);

  const focusSlug = useMemo(() => {
    if (!drillsByFilter) {
      return categorySlug;
    }

    const drilled = omitParentSlugs(appliedFacets.category ?? [], tree);
    if (isShopType && drilled.length > 1) {
      return undefined;
    }

    return drilled[0];
  }, [drillsByFilter, categorySlug, appliedFacets.category, tree, isShopType]);

  const liveCategorySlugs = useMemo(() => {
    const categoryFacet = drillsByFilter
      ? liveFacets?.get("category")
      : undefined;

    return categoryFacet?.type === "category"
      ? new Set(categoryFacet.data.map((item) => item.slug))
      : undefined;
  }, [drillsByFilter, liveFacets]);

  const view = useMemo(() => {
    const args = {
      tree,
      gender: resolvedGender,
      locale,
      categorySlug: focusSlug,
      buildHref,
      rootBack:
        rootLabel === undefined
          ? undefined
          : { label: rootLabel, href: filterHref() },
    };
    const level = getCategoryLevelView(args);

    if (!drillsByFilter || level.isLeaf) {
      return level;
    }

    const items = liveCategorySlugs
      ? level.items.filter((item) => liveCategorySlugs.has(item.slug))
      : level.items;

    return level.current && (!items.length || level.items.length === 1)
      ? getCategoryLevelView({ ...args, treatAsLeaf: true })
      : { ...level, items };
  }, [
    tree,
    resolvedGender,
    focusSlug,
    buildHref,
    filterHref,
    rootLabel,
    liveCategorySlugs,
    drillsByFilter,
  ]);

  return {
    view,
    isReady: drillsByFilter ? tree.length > 0 || hasFetched : Boolean(facets),
    drillsByFilter,
  };
}
